import React, { useState, useEffect } from 'react';
import {
  Card,
  Spin,
  message,
  Tag,
  Empty,
  Row,
  Col,
  Button,
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
  TagsOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const AlumnoSolicitudClase = () => {
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadTurnosDisponibles();
  }, []);

  const loadTurnosDisponibles = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/mi/turnos-disponibles');
      if (response.ok) {
        const data = await response.json();
        setTurnos(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar las clases disponibles');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las clases disponibles');
    } finally {
      setLoading(false);
    }
  };

  const getDiaSemana = (dia) => {
    // La BD usa: 1=Lunes, 2=Martes, ..., 6=Sábado, 7=Domingo
    const dias = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    return dias[dia] || '';
  };

  const getDiaSemanaFromDate = (fecha) => {
    if (!fecha) return '';
    const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    let fechaObj = dayjs(fecha);
    if (!fechaObj.isValid()) {
      fechaObj = dayjs(fecha, 'DD/MM/YYYY');
    }
    if (!fechaObj.isValid()) {
      console.error('Fecha inválida:', fecha);
      return '';
    }
    const diaIndex = fechaObj.day(); // dayjs: 0=Domingo, 1=Lunes, ..., 6=Sábado
    return dias[diaIndex] || '';
  };

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <h1 style={{ 
          color: '#ffffff', 
          marginBottom: isMobile ? '16px' : '24px',
          fontSize: isMobile ? '20px' : '24px',
        }}>
          <CalendarOutlined style={{ marginRight: '8px' }} />
          Solicitud Clase
        </h1>

        <Spin spinning={loading}>
          {turnos.length === 0 ? (
            <Empty 
              description="No hay clases disponibles en tu categoría" 
              style={{ marginTop: '40px' }}
            />
          ) : (
            <Row gutter={isMobile ? [12, 12] : [16, 16]}>
              {turnos.map((turno, index) => {
                const esFijo = turno.tur_tipo === 'FIJO';
                const esEvento = turno.tur_tipo === 'EVENTO';
                // Usar fecha_clase_display si está disponible (viene del backend), sino calcular
                const fechaDisplay = turno.fecha_clase_display || (esEvento 
                  ? dayjs(turno.tur_fecha_especifica).format('DD/MM/YYYY')
                  : '');
                const fechaClase = turno.fecha_clase || turno.tur_fecha_especifica;
                // Calcular el día de la semana desde la fecha real
                const diaSemanaDisplay = fechaClase ? getDiaSemanaFromDate(fechaClase) : '';
                const cantidadAsisten = turno.cantidad_alumnos_asisten || 0;
                const capacidadMaxima = turno.tur_cantidad_alumnos || null;

                return (
                  <Col xs={24} sm={12} lg={8} key={`${turno.tur_id}-${fechaClase || 'evento'}`}>
                    <Card
                      style={{
                        background: 'linear-gradient(135deg, #2d2d2d 0%, #3a3a3a 100%)',
                        border: '1px solid #404040',
                        borderRadius: '12px',
                        height: '100%',
                        minHeight: isMobile ? '280px' : '320px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                      }}
                      hoverable
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      bodyStyle={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        padding: isMobile ? '16px' : '20px',
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px',
                        flex: 1,
                      }}>
                        {/* Header con tipo de turno y categoría */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                          <Tag 
                            color={esFijo ? 'blue' : 'purple'}
                            style={{ fontSize: isMobile ? '11px' : '12px' }}
                          >
                            {esFijo ? 'Fijo' : 'Evento'}
                          </Tag>
                          {turno.categoria_color && (
                            <Tag 
                              color={turno.categoria_color}
                              style={{ fontSize: isMobile ? '11px' : '12px' }}
                            >
                              <TagsOutlined style={{ marginRight: '4px' }} />
                              {turno.categoria_descripcion}
                            </Tag>
                          )}
                        </div>

                        {/* Información de fecha y hora */}
                        <div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            marginBottom: '8px',
                            color: '#ffffff',
                            fontSize: isMobile ? '16px' : '18px',
                            fontWeight: 600,
                          }}>
                            <CalendarOutlined />
                            {esFijo ? (
                              <span>{diaSemanaDisplay}</span>
                            ) : (
                              <span>{fechaDisplay}</span>
                            )}
                          </div>
                          {esFijo && (
                            <div style={{ 
                              fontSize: isMobile ? '12px' : '13px', 
                              color: '#b0b0b0',
                              marginLeft: '24px',
                              marginBottom: '8px',
                            }}>
                              Fecha: {fechaDisplay}
                            </div>
                          )}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            color: '#e0e0e0',
                            fontSize: isMobile ? '13px' : '14px',
                            marginLeft: esFijo ? '24px' : '0',
                          }}>
                            <ClockCircleOutlined />
                            {turno.tur_hora_desde} - {turno.tur_hora_hasta}
                          </div>
                        </div>

                        {/* Cancha */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                        }}>
                          <EnvironmentOutlined />
                          {turno.cancha_descripcion || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>Sin asignar</span>}
                        </div>

                        {/* Profesor */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                        }}>
                          <UserOutlined />
                          {turno.profesor_nombre && turno.profesor_apellido ? (
                            <span>{turno.profesor_nombre} {turno.profesor_apellido}</span>
                          ) : (
                            <span style={{ fontStyle: 'italic', opacity: 0.6 }}>Sin asignar</span>
                          )}
                        </div>

                        {/* Cantidad de alumnos que asisten / Capacidad máxima */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          marginTop: '4px',
                        }}>
                          <TeamOutlined />
                          <span>
                            {capacidadMaxima 
                              ? `${cantidadAsisten} / ${capacidadMaxima} alumnos`
                              : `${cantidadAsisten} alumno${cantidadAsisten !== 1 ? 's' : ''}`}
                          </span>
                        </div>

                        {/* Espaciador flexible */}
                        <div style={{ flex: 1 }} />

                        {/* Botón para solicitar asistir */}
                        <Button
                          type="primary"
                          onClick={async () => {
                            try {
                              const response = await apiClient.post('/mi/solicitud-clase', {
                                turnoId: turno.tur_id,
                                fechaClase: fechaClase,
                                observaciones: null,
                              });

                              if (response.ok) {
                                message.success('Solicitud enviada correctamente');
                                loadTurnosDisponibles();
                              } else {
                                const error = await response.json();
                                message.error(error.message || 'Error al enviar la solicitud');
                              }
                            } catch (error) {
                              console.error('Error:', error);
                              message.error('Error al enviar la solicitud');
                            }
                          }}
                          style={{
                            marginTop: 'auto',
                            width: '100%',
                          }}
                          size={isMobile ? 'small' : 'middle'}
                        >
                          Solicitar asistir
                        </Button>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Spin>
      </div>
    </MainLayout>
  );
};

export default AlumnoSolicitudClase;
